import { LockKeyIcon } from '@phosphor-icons/react'
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '@curri/components'
import { useState, type ComponentType, type ReactNode } from 'react'

import './webhooks.css'

import type { Resource } from '../../api/client.ts'
import {
  WEBHOOK_READ_SCOPE,
  WEBHOOK_WRITE_SCOPE,
  type WebhookEndpointConfig,
} from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { GhostBar } from '../../ghost/GhostBar.tsx'
import { useGhost } from '../../ghost/GhostProvider.tsx'
import { BarcodeStamp } from '../landing/Barcode.tsx'

import { DeliveryWebhooksPane } from './DeliveryWebhooksPane.tsx'
import { PayloadWebhooksPane } from './PayloadWebhooksPane.tsx'
import { SegmentedControl } from './SegmentedControl.tsx'
import { SignedOut } from './SignedOut.tsx'
import {
  isWebhookTypeId,
  WEBHOOK_TYPE_IDS,
  WEBHOOK_TYPES,
  type WebhookTypeId,
} from './types.ts'
import { useWebhookConfig } from './useWebhookData.ts'

// The webhooks dashboard.
//
// Answers the three questions that currently route through a Curri employee: is
// my endpoint configured, did you send it, and what did my server say back.

const Page = ({ children }: { children: ReactNode }) => (
  // The shell's pane is `overflow: hidden`, so the screen owns its own scroll.
  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
    <div
      style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '40px 32px 64px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {children}
    </div>
  </div>
)

// The design stamps section headings with a barcode rule and a plan-sheet
// serial (see landing/Barcode.tsx — the bars scan as the serial's number).
const PageHeading = () => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <h1 style={{ fontSize: 25 }}>Webhook Dashboard</h1>
      <span style={{ flex: 1 }} />
      <BarcodeStamp tone="muted" />
    </div>
    <p
      style={{
        margin: '12px 0 0',
        fontSize: 15,
        lineHeight: 1.6,
        color: 'var(--curri-text-secondary)',
        maxWidth: '62ch',
        textWrap: 'pretty',
      }}
    >
      What Curri sent to your endpoint, and how it responded. Open any attempt
      to see the exact body that was POSTed.
    </p>
  </div>
)

/**
 * Rendered when the session's token was issued without `webhook:read`.
 *
 * Checked up front against the granted scopes rather than waiting for three
 * 403s: an empty table with an error banner reads as "your webhooks are broken",
 * which is the wrong diagnosis and the wrong next step.
 */
const MissingScope = () => {
  const { signOut } = useAuth()

  return (
    <Page>
      <PageHeading />
      <EmptyState variant="warning">
        <EmptyStateIcon>
          <LockKeyIcon />
        </EmptyStateIcon>
        <EmptyStateTitle>This session cannot read webhook history</EmptyStateTitle>
        <EmptyStateDescription>
          Your access token was issued without the <code>{WEBHOOK_READ_SCOPE}</code>{' '}
          scope. Signing in again will request it. If it still does not appear,
          the OAuth client this portal uses needs <code>{WEBHOOK_READ_SCOPE}</code>{' '}
          added to its registration — email engineering@curri.com.
        </EmptyStateDescription>
        <EmptyStateActions>
          <Button label="Sign out and try again" onClick={signOut} size="base" />
        </EmptyStateActions>
      </EmptyState>
    </Page>
  )
}

interface PaneProps {
  canEdit: boolean
  config: { resource: Resource<WebhookEndpointConfig[]>; refresh: () => void }
  windowDays: number
  onNavigate: (path: string) => void
  onWindowChange: (next: number) => void
}

const PANES: Record<WebhookTypeId, ComponentType<PaneProps>> = {
  deliveries: DeliveryWebhooksPane,
  payloads: PayloadWebhooksPane,
}

const initialType = (): WebhookTypeId => {
  const requested = new URLSearchParams(window.location.search).get('type')
  return isWebhookTypeId(requested) ? requested : 'deliveries'
}

const Dashboard = ({
  grantedScopes,
  onNavigate,
}: {
  grantedScopes: string[]
  onNavigate: (path: string) => void
}) => {
  const [windowDays, setWindowDays] = useState(7)
  const [type, setType] = useState<WebhookTypeId>(initialType)

  // One shared /config read: both panes show endpoints drawn from it, filtered
  // to the variants their own type owns.
  const config = useWebhookConfig()

  // Ghost sessions are read-only on the server (`Curri-As-User` rejects every
  // non-GET), so the editor is hidden rather than left to fail on save.
  const { target: ghostTarget } = useGhost()
  const canEdit =
    grantedScopes.includes(WEBHOOK_WRITE_SCOPE) && ghostTarget === null

  const changeType = (next: WebhookTypeId) => {
    setType(next)

    const url = new URL(window.location.href)
    if (next === 'deliveries') {
      url.searchParams.delete('type')
    } else {
      url.searchParams.set('type', next)
    }
    window.history.replaceState(null, '', url)
  }

  const Pane = PANES[type]

  return (
    <Page>
      <PageHeading />
      {/* Above the data, not beside it: whoever is reading this screen has to
          know whose data it is before they read any of it. Renders nothing for a
          partner session. */}
      <GhostBar />
      <SegmentedControl
        ariaLabel="Webhook type"
        onChange={changeType}
        options={WEBHOOK_TYPE_IDS.map(id => ({
          label: WEBHOOK_TYPES[id].label,
          value: id,
        }))}
        value={type}
      />
      <Pane
        canEdit={canEdit}
        config={config}
        onNavigate={onNavigate}
        onWindowChange={setWindowDays}
        windowDays={windowDays}
      />
    </Page>
  )
}

export const Webhooks = ({
  onNavigate,
}: {
  onNavigate: (path: string) => void
}) => {
  const { state } = useAuth()

  // This screen owns its signed-out state rather than falling through to the
  // shell's generic placeholder: the pitch for the dashboard lives where the
  // dashboard would be.
  if (state.status !== 'authenticated') {
    return <SignedOut onNavigate={onNavigate} />
  }

  return state.caller.grantedScopes.includes(WEBHOOK_READ_SCOPE) ? (
    <Dashboard
      grantedScopes={state.caller.grantedScopes}
      onNavigate={onNavigate}
    />
  ) : (
    <MissingScope />
  )
}
